import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Tabs,
  Tab,
  TextField,
  Button,
  Chip,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import PublishIcon from '@material-ui/icons/Publish';
import Alert from '@material-ui/lab/Alert';
import { MarkdownContent } from '@backstage/core-components';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { ServiceEntry } from '../promoteUtils';

const useStyles = makeStyles((theme) => ({
  editorTextarea: {
    '& .MuiOutlinedInput-root': {
      alignItems: 'flex-start',
    },
    '& .MuiOutlinedInput-inputMultiline': {
      minHeight: 360,
      maxHeight: 'calc(100vh - 400px)',
      overflow: 'auto !important',
      fontFamily: 'monospace',
      fontSize: 12.5,
      lineHeight: 1.6,
    },
  },
  preview: {
    padding: theme.spacing(2),
    minHeight: 360,
    maxHeight: 'calc(100vh - 400px)',
    overflow: 'auto',
  },
  rail: {
    padding: theme.spacing(2),
  },
  railLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  sourceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    fontSize: 12,
  },
  serviceName: {
    fontFamily: 'monospace',
    fontWeight: 600,
  },
}));

interface GqlResponse<T> {
  data?: T;
  errors?: { message: string }[];
}

export type PublishState = 'idle' | 'publishing' | 'done' | 'error';

export interface StepReviewProps {
  markdown: string;
  setMarkdown: (md: string) => void;
  bundleVersion: string;
  setBundleVersion: (v: string) => void;
  publishState: PublishState;
  setPublishState: (s: PublishState) => void;
  publishError: string | null;
  setPublishError: (e: string | null) => void;
  selectedServices: ServiceEntry[];
  noteCounts: Record<string, number>;
}

export const StepReview = ({
  markdown,
  setMarkdown,
  bundleVersion,
  setBundleVersion,
  publishState,
  setPublishState,
  publishError,
  setPublishError,
  selectedServices,
  noteCounts,
}: StepReviewProps) => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const { fetch: backstageFetch } = useApi(fetchApiRef);
  const graphqlUrl = `${config.getString('backend.baseUrl')}/api/proxy/release-notes/graphql`;

  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [loadingCurrent, setLoadingCurrent] = useState(true);

  const gql = useCallback(
    async <T,>(query: string, variables?: Record<string, unknown>): Promise<GqlResponse<T>> => {
      const res = await backstageFetch(graphqlUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, variables }),
      });
      return res.json();
    },
    [backstageFetch, graphqlUrl],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await gql<{ currentRelease: { version: string } | null }>(`
          query { currentRelease { version } }
        `);
        if (cancelled) return;
        setCurrentVersion(result.data?.currentRelease?.version ?? null);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoadingCurrent(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gql]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper variant="outlined" style={{ padding: 12 }}>
          <Box display="flex" alignItems="center" gridGap={16} flexWrap="wrap">
            <Box display="flex" alignItems="center" gridGap={8}>
              <Typography variant="body2" color="textSecondary">
                Current version:
              </Typography>
              {loadingCurrent ? (
                <CircularProgress size={12} />
              ) : currentVersion ? (
                <Chip size="small" label={currentVersion} />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  none
                </Typography>
              )}
            </Box>
            <TextField
              label="New bundle version"
              placeholder="e.g. 2.4.0"
              variant="outlined"
              size="small"
              value={bundleVersion}
              onChange={(e) => setBundleVersion(e.target.value)}
              style={{ width: 220 }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={publishState === 'publishing' ? <CircularProgress size={14} /> : <PublishIcon />}
              disabled={
                !bundleVersion.trim() ||
                !markdown.trim() ||
                publishState === 'publishing' ||
                publishState === 'done'
              }
              onClick={async () => {
                setPublishError(null);
                setPublishState('publishing');
                try {
                  const result = await gql<{ createRelease: { id: number; version: string } }>(
                    `mutation CreateRelease($version: String!, $changelog: String!) {
                      createRelease(version: $version, changelog: $changelog) { id version }
                    }`,
                    { version: bundleVersion.trim(), changelog: markdown },
                  );
                  if (result.errors) {
                    setPublishError(result.errors[0].message);
                    setPublishState('error');
                    return;
                  }
                  setPublishState('done');
                } catch (err: any) {
                  setPublishError(err.message || 'network error');
                  setPublishState('error');
                }
              }}
            >
              {publishState === 'done' ? 'Published' : 'Publish'}
            </Button>
            {publishState === 'done' && (
              <Chip size="small" color="primary" label={`Bundle ${bundleVersion.trim()} published`} />
            )}
          </Box>
        </Paper>
      </Grid>

      {publishError && (
        <Grid item xs={12}>
          <Alert severity="error">Publish failed: {publishError}</Alert>
        </Grid>
      )}

      <Grid item xs={12} md={8}>
        <Paper variant="outlined">
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab value="edit" label="Edit" />
            <Tab value="preview" label="Preview" />
          </Tabs>
          {tab === 'edit' ? (
            <Box p={1}>
              <TextField
                className={classes.editorTextarea}
                multiline
                fullWidth
                variant="outlined"
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                placeholder="# Release notes"
              />
            </Box>
          ) : (
            <Box className={classes.preview}>
              {markdown.trim() ? (
                <MarkdownContent content={markdown} />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Nothing to preview yet.
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper variant="outlined" className={classes.rail}>
          <div className={classes.railLabel}>Sources</div>
          {selectedServices.map((s) => (
            <div key={s.name} className={classes.sourceRow}>
              <span className={classes.serviceName} style={{ flex: 1 }}>
                {s.name}
              </span>
              <Typography variant="caption" color="textSecondary">
                {noteCounts[s.name] ?? 0}
              </Typography>
            </div>
          ))}
        </Paper>
        <Box mt={2}>
          <Paper variant="outlined" className={classes.rail}>
            <div className={classes.railLabel}>Publish target</div>
            <Typography variant="body2">
              <strong>release-notes</strong>
            </Typography>
            <Typography variant="caption" color="textSecondary">
              mutation createRelease
            </Typography>
          </Paper>
        </Box>
      </Grid>
    </Grid>
  );
};
